/**
 * Types inventaire joueur (cf wiki §24.12). Aligne 1:1 sur MeDtos.cs:MeItemEntry
 * / MeItemsDto cote backend.
 */

/** EItemKind backend serialise en string. */
export type ItemKind =
  | 'RessourceConsommable'
  | 'Document'
  | 'Cle'
  | 'Scientifique'
  | 'Artifact'
  | 'Relic'
  | 'ComponentPart';

export const ITEM_KIND_LABELS: Record<ItemKind, string> = {
  RessourceConsommable: 'Ressource',
  Document: 'Document',
  Cle: 'Clé',
  Scientifique: 'Scientifique',
  Artifact: 'Artefact',
  Relic: 'Relique',
  ComponentPart: 'Composant',
};

/** Couleur de tag Element Plus par kind. */
export const ITEM_KIND_TONE: Record<ItemKind, 'info' | 'success' | 'warning' | 'danger' | 'primary'> = {
  RessourceConsommable: 'info',
  Document: 'primary',
  Cle: 'warning',
  Scientifique: 'info',
  Artifact: 'success',
  Relic: 'danger',
  ComponentPart: 'info',
};

/** Tier de rarete tel que stocke en DB (0..3 -> commune..legendaire). */
export const ITEM_RARITY_LABELS: Record<number, string> = {
  0: 'Commun',
  1: 'Rare',
  2: 'Épique',
  3: 'Légendaire',
};

export const ITEM_RARITY_TONE: Record<number, 'info' | 'success' | 'warning' | 'danger'> = {
  0: 'info',
  1: 'success',
  2: 'warning',
  3: 'danger',
};

/** Affixe secondaire rolled au drop (cf wiki §24.12 Diablo-like). */
export interface MeItemAffix {
  key: string;
  value: number;
  /** Libellé CLAIR de l'effet (ex. « Efficacité carburant ») ; vide = repli sur key. */
  label?: string;
  /** La valeur s'exprime en pourcentage (suffixe « % »). */
  isPercent?: boolean;
  /** true = plus c'est haut, mieux c'est (couleur des deltas). */
  higherIsBetter?: boolean;
  /** Borne basse du roll possible (plage du type) ; null/absent = pas de barre. */
  min?: number | null;
  /** Borne haute du roll possible (plage du type) ; avec `min`, alimente la barre. */
  max?: number | null;
}

export interface MeItemEntry {
  itemId: number;
  playerUniverseId: number;
  quantity: number;
  acquiredAt?: string | null;
  origin?: string | null;

  itemTypeId: number;
  code: string;
  label: string;
  /** Nom GÉNÉRÉ de l'instance (items procéduraux). Null = retombe sur label. */
  name?: string | null;
  description: string;
  kind: ItemKind;
  /** Rarete de l'INSTANCE rolled (0..3), pas du template. */
  rarity: number;
  isTradable: boolean;
  imageCode?: string | null;
  requiredGlyphId?: number | null;
  /** Set d'objets (artefacts/reliques) ; null = hors set. */
  setId?: number | null;
  /** Taille au musée (1..4) : emplacements consommés une fois exposé (artefacts/
   *  reliques). Alimente les hexagones de taille du tooltip, unifiés avec les composants. */
  museumSize?: number | null;

  /** Bonus principal rolled (Artifact / Relic). null pour les autres kinds. */
  bonusCode?: string | null;
  bonusValue: number;
  /** Portee du bonus (Relic) : planet / universe / player. */
  bonusScope?: string | null;
  /** Bornes de la plage de roll du bonus principal (courbe attribut × rareté) ;
   *  avec la valeur, alimentent la barre du tooltip. null = pas de barre. */
  bonusMin?: number | null;
  bonusMax?: number | null;
  /** Document : recherche débloquée (révélée). Id + libellé ; null pour les autres kinds. */
  targetResearchId?: number | null;
  targetResearchLabel?: string | null;
  /** Affixes secondaires rolled (0..3). */
  affixes: MeItemAffix[];
}

/**
 * Libellés FR des CLÉS d'affixe procédurales (snake_case) — miroir du catalogue
 * `attribute_rarity_curves.Label` (seed). Le joueur ne doit jamais voir le code
 * brut (« yield_pct »). Garder synchro avec le CASE du seed.
 */
export const AFFIX_KEY_LABELS: Record<string, string> = {
  attack_pct: 'Attaque (%)',
  dodge_pct: 'Esquive (%)',
  defense_pct: 'Défense (%)',
  damage_resistance_pct: 'Rés. dégâts (%)',
  armor_value: 'Blindage',
  hp_pct: 'Points de vie (%)',
  heat_pct: 'Rés. thermique (%)',
  speed: 'Vitesse',
  fuel_consumption_pct: 'Conso carburant (%)',
  commerce_pct: 'Commerce (%)',
  revenue_pct: 'Revenus (%)',
  yield_pct: 'Rendement (%)',
  population_growth_pct: 'Croissance pop. (%)',
  well_being_pct: 'Bien-être (%)',
  build_speed_pct: 'Vitesse constr. (%)',
  research_pct: 'Recherche (%)',
  bonus_global_pct: 'Bonus global (%)',
  boost_pct: 'Boost (%)',
};

/**
 * Formatte une cle d'affixe en libelle humain. Priorité au catalogue FR
 * (AFFIX_KEY_LABELS) ; sinon repli title-case (« fire_rate_pct » -> « Fire rate
 * pct »). Les libellés déjà humains (affixes de composant) passent tels quels.
 */
export function formatAffixKey(key: string): string {
  if (!key) return '';
  const mapped = AFFIX_KEY_LABELS[key.toLowerCase()];
  const label = mapped ?? (() => {
    const words = key.replace(/_/g, ' ').trim();
    return words.charAt(0).toUpperCase() + words.slice(1);
  })();
  // La valeur affichée à côté porte déjà « % » (isPercent / suffixe _pct) : on
  // retire le « (%) » redondant du libellé (« Revenus (%) » -> « Revenus »).
  return label.replace(/\s*\(%\)\s*$/, '');
}

/**
 * Formatte une valeur d'affixe en respectant le suffixe (_pct => "%",
 * valeurs signed => +/-).
 */
export function formatAffixValue(key: string, value: number): string {
  const isPct = key.endsWith('_pct');
  const sign = value > 0 ? '+' : '';
  return isPct ? `${sign}${value}%` : `${sign}${value}`;
}

export interface MeItemsDto {
  playerUniverseId: number;
  items: MeItemEntry[];
}
